import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "334",
  "slug": "deepest-places-on-earth",
  "title": "The Deepest Places on Earth: Trenches, Caves, Mines, and One Very Deep Hole",
  "excerpt": "From the bottom of the Mariana Trench to a Soviet borehole that unsettled its own scientists, here are the deepest points humans have found — and the few we've actually reached.",
  "date": "2026-06-03",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1530053969600-caed2596d242?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxkZWVwJTIwb2NlYW58ZW58MXwwfHx8MTc4MDQ3NzM1OXww&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "5 min",
  "author": "EarthGuessr Team",
  "tldr": "The deepest natural point is the Challenger Deep in the Mariana Trench, almost 11 kilometres below the sea surface. On land, the records split up: the deepest cave (Veryovkina, about 2.2 km), the deepest mine where people work (Mponeng, about 4 km), and the deepest hole ever drilled (the Kola Superdeep Borehole, about 12.3 km).",
  "content": [
    {
      "type": "paragraph",
      "text": "We map mountains obsessively — everyone knows Everest is the highest point on land. But ask about the deepest places on Earth and most people draw a blank, partly because there's no single answer. Deepest ocean, deepest cave, deepest mine, deepest hole humans have drilled: each is a different record, and each tells you something about how far down the planet goes and how far down we've managed to follow."
    },
    {
      "type": "heading",
      "text": "The Deepest Point in the Ocean: Challenger Deep"
    },
    {
      "type": "paragraph",
      "text": "The Challenger Deep, at the southern end of the Mariana Trench in the western Pacific, is the deepest known point on Earth — close to 11 kilometres below the surface. The pressure down there is over a thousand times what you feel at sea level, enough to crush almost anything not built specifically to survive it. Humans have visited only a handful of times: first aboard the bathyscaphe Trieste in 1960, and again in 2012 when filmmaker James Cameron descended solo. More people have walked on the Moon than have reached the bottom of the ocean."
    },
    {
      "type": "heading",
      "text": "The Deepest Cave: Veryovkina"
    },
    {
      "type": "paragraph",
      "text": "The deepest known cave on Earth is Veryovkina, in the Caucasus mountains of the Abkhazia region of Georgia. It bottoms out at over 2,200 metres below its entrance — deep enough to swallow several stacked skyscrapers. It only recently took the record from nearby Krubera Cave, and the two sit in the same limestone massif, riddled with vertical shafts that cavers spend weeks descending and climbing back out of."
    },
    {
      "type": "heading",
      "text": "The Deepest Place Humans Actually Work: Mponeng"
    },
    {
      "type": "paragraph",
      "text": "Down where Veryovkina's explorers turn around, no one lives. But people go to work every day around four kilometres underground at the Mponeng gold mine in South Africa, one of the deepest mines on Earth. The rock at that depth is hot enough — well over 50 degrees Celsius — that the mine has to pump in slurried ice just to keep conditions survivable. It's a strange thought: a daily commute that ends deeper than most caves go."
    },
    {
      "type": "heading",
      "text": "The Deepest Hole We Ever Drilled: Kola"
    },
    {
      "type": "paragraph",
      "text": "In the 1970s and 80s, Soviet scientists drilled the Kola Superdeep Borehole on the Kola Peninsula, near the Norwegian border, purely to see how far down they could go. They reached about 12,262 metres before the heat — far higher than predicted, around 180 degrees Celsius — turned the rock plastic and gummed up the drill. Along the way they found flowing water far deeper than anyone expected and microscopic fossils kilometres down. The hole was eventually sealed, and it's still the deepest artificial point on Earth, a narrow shaft barely wider than a grapefruit."
    },
    {
      "type": "heading",
      "text": "And the Lowest Land You Can Stand On"
    },
    {
      "type": "paragraph",
      "text": "If you want depth you can actually walk to, head for the shore of the Dead Sea, the lowest dry land on the planet at roughly 430 metres below sea level — and still dropping as the sea shrinks. And the deepest land point of all is hidden under ice: the Bentley Subglacial Trench in Antarctica reaches about 2,500 metres below sea level, buried beneath the ice sheet."
    },
    {
      "type": "heading",
      "text": "Why We Know So Little About the Deep"
    },
    {
      "type": "paragraph",
      "text": "Here's the strange part: we have sharper maps of the surface of Mars and the Moon than we do of our own seafloor. Sunlight doesn't reach the deep ocean, radio waves don't travel through water, and the pressure destroys all but the most specialised equipment, so most of the abyss has only ever been mapped at coarse resolution, inferred from tiny bumps satellites detect on the sea surface. Detailed, ship-based mapping of the deep ocean is still very much a work in progress. The deepest places on Earth are, in a real sense, the least explored."
    },
    {
      "type": "heading",
      "text": "Life at the Bottom"
    },
    {
      "type": "paragraph",
      "text": "And yet none of these depths is empty. The Challenger Deep is home to pale, shrimp-like amphipods and single-celled organisms that thrive under crushing pressure and total darkness, feeding on whatever drifts down from far above. Deep mines and caves host their own microbes living off rock chemistry rather than sunlight. The one dispiriting discovery: explorers have found human-made plastic at the very bottom of the Mariana Trench, proof that there is no longer anywhere on the planet truly beyond our reach."
    },
    {
      "type": "heading",
      "text": "Reaching the Bottom Is Newer Than Spaceflight"
    },
    {
      "type": "paragraph",
      "text": "Here's a fact that reorders your sense of exploration: humans walked on the Moon decades before anyone could routinely reach the deepest ocean. For most of the half-century after the Trieste's 1960 dive, the bottom of the Mariana Trench went unvisited. It wasn't until the Five Deeps Expedition around 2019 that a crewed submersible reached the deepest point of all five oceans and made repeat dives almost ordinary. The deep sea, right here on our own planet, was harder to get to than orbit."
    },
    {
      "type": "heading",
      "text": "Why the Depths Shape the Surface"
    },
    {
      "type": "paragraph",
      "text": "These deep places aren't just curiosities — they drive the world above them. Ocean trenches like the Mariana mark where one slab of the Earth's crust is grinding down beneath another, and that same process builds the volcanoes and triggers the earthquakes of the Pacific Ring of Fire. The deepest scars in the planet and its most violent surface activity are two ends of a single story, which is why a trench on the seafloor so often has a chain of volcanoes running parallel to it on land."
    }
  ]
};
